import "./SignOutButton.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, Ref, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";

import { Case, CaseDiagnosis, CaseReport, User } from "pwapi";

import { requestSignout } from "shared/actions/caseDetailActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";

import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";

import {
  relevantSlidesViewedOnCase,
  relevantSpecimenDiagnosedOnCase,
  hasAnyDiagnosticInformation,
  validityOfDiagnosis,
} from "./DiagnosisDialogHelpers";
import LocationCodeSelector from "./LocationCodeSelector";
import {
  invalidCaseReasons,
  isModifyingReport,
  softDiagnosisValidationReasons,
} from "./utils";
import { SignoutAlertModal } from "../modals/AlertModal";

interface SignOutButtonProps {
  caseDetailObject: CaseDetailState;
  currentSlideId?: number;
  label?: string;
  user?: User;
  selectedSignatureId?: number;
  formErrors?: boolean;
  showSpinner?: boolean;
  isPrelim?: boolean;
  signOutTipPlace?: "top" | "left" | "right" | "bottom";
  isTechnical?: boolean;
}

const SignOutButton = forwardRef(
  (props: SignOutButtonProps, ref: Ref<HTMLButtonElement>) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
      caseDetailObject,
      currentSlideId,
      label,
      user,
      selectedSignatureId,
      formErrors,
      showSpinner,
      isPrelim,
      signOutTipPlace,
      isTechnical,
    } = props;
    const [signoutAlert, setSignoutAlert] = useState(false);
    const [signingOut, setSigningOut] = useState<boolean>(false);

    const features = useSelector(({ sys }: ApplicationState) => sys.features);

    const locationCodes = useSelector(
      ({ auth }: ApplicationState) => auth.user?.location_codes
    );
    const locationCodeSelect = useRef<HTMLSelectElement>(null);

    const isModifyingRep = isModifyingReport(caseDetailObject, features, user);
    const userSignatures =
      caseDetailObject.source === Case.source.PW
        ? useSelector(
            ({ userSignatures }: ApplicationState) =>
              userSignatures?.[
                isModifyingRep?.source === CaseDiagnosis.source.CORRECTION
                  ? caseDetailObject.report_signed_signature?.user_id || 0
                  : user?.id || 0
              ]
          )
        : undefined;

    // Take the selected signature, if it exists.
    // Otherwise, drop back to previous behaviour
    // This is for backwards-compatibility for the group of cases that won't have a
    // finalizing_signature_id when we first roll this out
    // TODO: @James feels like a lot of duplication between here and CaseReport.tsx
    const defaultSignature =
      userSignatures?.find(
        (s) => s.id === caseDetailObject.finalizing_signature_id
      ) || userSignatures?.[0];

    const allSlidesViewed = relevantSlidesViewedOnCase(
      caseDetailObject,
      currentSlideId
    );

    const allAreDiagnosed =
      isPrelim ||
      isTechnical ||
      relevantSpecimenDiagnosedOnCase(
        caseDetailObject,
        features?.screenPerSpecimen
      );

    const skipEnterKeyUnlessShift = (e: React.KeyboardEvent<HTMLElement>) => {
      // Disable Enter key unless Shift is held
      const enter = e.key === "Enter" || e.code === "Enter" || e.keyCode === 13;
      if (enter && !e.shiftKey) e.preventDefault();
    };

    const skipSpaceKey = (e: React.KeyboardEvent<HTMLElement>) => {
      // Disable Space Key
      const space = e.code === "Space";
      if (space) e.preventDefault();
    };

    const caseDiagnoses = caseDetailObject.specimenDetail
      .map((s) => {
        const specimensDiagnosis = s.diagnoses?.filter(
          (d) => d.user?.id === user?.id && d.is_completed === false
        );
        return specimensDiagnosis && specimensDiagnosis.length
          ? specimensDiagnosis[0]
          : undefined;
      })
      .filter((d) => d);

    const hasAnyDiagnosis = useMemo(() => {
      return !!caseDiagnoses?.find((d) => d && hasAnyDiagnosticInformation(d));
    }, [caseDiagnoses]);

    const invalidDiagnoses = useMemo(() => {
      return isPrelim || isTechnical
        ? []
        : caseDiagnoses
            ?.map((d) => d && validityOfDiagnosis(d))
            .filter((d) => d && !d.valid) || [];
    }, [caseDiagnoses, isTechnical]); // TODO: @James isPrelim was missing from deps here

    const missingSignatures =
      !caseDetailObject.is_screening &&
      caseDetailObject.source === Case.source.PW &&
      !(selectedSignatureId || (userSignatures && userSignatures.length > 0));

    const buttonIsDisabled =
      (isModifyingReport(caseDetailObject, features, user)?.source !==
        CaseDiagnosis.source.CORRECTION &&
        !isPrelim &&
        !isTechnical &&
        !allSlidesViewed) ||
      (isPrelim && !hasAnyDiagnosis) ||
      (!isTechnical && !allAreDiagnosed) ||
      (!isTechnical && !!invalidDiagnoses.length) ||
      caseDetailObject.diagnosis_status === "loading" ||
      formErrors ||
      signingOut ||
      (!isTechnical && missingSignatures);

    const buttonTooltip = [
      isModifyingReport(caseDetailObject, features, user)?.source !==
        CaseDiagnosis.source.CORRECTION &&
        !isPrelim &&
        !isTechnical &&
        !allSlidesViewed &&
        "Unviewed Slides",
      !allAreDiagnosed && "Undiagnosed Specimen",
      ...(isPrelim && !hasAnyDiagnosis
        ? ["Missing any diagnostic information on any specimen"]
        : []),
      !!invalidDiagnoses.length &&
        invalidDiagnoses
          .map(
            (v) =>
              `Specimen ${
                caseDetailObject.specimenDetail.find(
                  (s) => s.id === v?.specimen
                )?.name
              } missing ${v?.missing.join(", ")}`
          )
          .join("\n"),
      missingSignatures && "Missing Signature",
    ]
      .filter((v) => !!v)
      .join(", ");

    const invalidReasons = invalidCaseReasons(caseDetailObject, features);
    const softDiagnosisValidations = softDiagnosisValidationReasons(
      caseDetailObject,
      caseDiagnoses
    );

    const signOutCase = () => {
      if (!buttonIsDisabled) {
        const modifyingReport = isModifyingReport(
          caseDetailObject,
          features,
          user
        );
        // Use the *value that is in the actual dropdown*
        // ... So we're not playing games with localStorage or store state
        // ... The value of the input is the user's intention.
        const locationCodeId = locationCodeSelect.current?.value
          ? !Number.isNaN(locationCodeSelect.current?.value)
            ? Number(locationCodeSelect.current?.value)
            : undefined
          : undefined;
        caseDetailObject?.id &&
          dispatch(
            requestSignout(
              caseDetailObject.id,
              navigate,
              caseDetailObject.source === Case.source.PW && !isTechnical
                ? selectedSignatureId || defaultSignature?.id
                : undefined,
              isTechnical
                ? CaseReport.report_type.TECHNICAL
                : isPrelim
                ? CaseReport.report_type.PRELIMINARY
                : modifyingReport?.source
                ? modifyingReport?.source === CaseDiagnosis.source.AMEND
                  ? CaseReport.report_type.AMEND
                  : modifyingReport?.source === CaseDiagnosis.source.ADDEND
                  ? CaseReport.report_type.ADDEND
                  : CaseReport.report_type.CORRECTION
                : caseDetailObject.is_screening
                ? CaseReport.report_type.SCREEN
                : CaseReport.report_type.FINALIZING,
              isPrelim || isTechnical ? undefined : locationCodeId
            )
          );
        setSigningOut(true);
      }
    };

    const onHandleSignOut = (e: React.FormEvent) => {
      e.preventDefault();

      if (isTechnical && caseDetailObject.status === Case.status.WORKING) {
        setSignoutAlert(true);
      } else if (
        !isPrelim &&
        (invalidReasons?.length ||
          softDiagnosisValidations?.length ||
          caseDetailObject.status === Case.status.WORKING)
      ) {
        setSignoutAlert(true);
      } else if (
        (isPrelim && caseDetailObject.status === Case.status.WORKING) ||
        caseDetailObject.status === Case.status.ACTIVE ||
        caseDetailObject.status === Case.status.FINALIZED
      ) {
        signOutCase();
      }
    };

    return (
      <div className="SignOutButton--container">
        {caseDetailObject.source === Case.source.PW &&
          !caseDetailObject.is_screening &&
          !isPrelim &&
          !isTechnical &&
          !!locationCodes?.length && (
            <LocationCodeSelector ref={locationCodeSelect} />
          )}
        <button
          className="SignOutButton SignOutButton--noBorder"
          type="button"
          disabled={buttonIsDisabled}
          onKeyDown={skipEnterKeyUnlessShift}
          onKeyUp={skipSpaceKey}
          onClick={onHandleSignOut}
          title={buttonTooltip || undefined}
          ref={ref}
        >
          {showSpinner && caseDetailObject.diagnosis_status === "loading" && (
            <Spinner
              size={"16px"}
              width={"3px"}
              backColor={"var(--blue15)"}
              frontColor={"var(--blue)"}
            />
          )}
          {label ||
            (isTechnical && "Issue Technical Report") ||
            (isPrelim && "Issue Preliminary Report") ||
            (caseDetailObject.is_screening && "Screen") ||
            "Sign Out"}
        </button>
        {features?.useMergeCaseReportWorkflow &&
          !!caseDetailObject.documents.find((d) => d.attach_to_case_report) && (
            <div
              className="SignOutButtonTip SignOutButton--noBorder"
              data-for="signout_button_tip"
              data-event="click"
              data-event-off="mouseout"
              data-tip=""
            >
              <FontAwesomeIcon icon="paperclip" />
            </div>
          )}
        <ReactTooltip
          id="signout_button_tip"
          effect="solid"
          backgroundColor="transparent"
          borderColor="transparent"
          textColor="black"
          padding="0"
          place={signOutTipPlace || "top"}
          overridePosition={(
            { left, top },
            _currentEvent,
            _currentTarget,
            node
          ) => {
            // This code comes from the V4 docs of ReactTooltip to attempt to keep the tooltip on the screen
            // TODO: Remove this when V5 is available
            const d = document.documentElement;
            left = Math.min(d.clientWidth - (node?.clientWidth || 0), left);
            top = Math.min(d.clientHeight - (node?.clientHeight || 0), top);
            left = Math.max(0, left);
            top = Math.max(0, top);
            return { top, left };
          }}
          delayHide={750}
        >
          <div className="SignOutTip">
            <span className="SignOutTip--header">
              Documents Attached to Report
            </span>
            {caseDetailObject?.documents
              .filter((d) => d.attach_to_case_report)
              .map((d) => (
                <span key={d.id} className="SignOutTip--line">
                  {d.name}
                </span>
              ))}
          </div>
        </ReactTooltip>
        {(caseDetailObject.status === Case.status.WORKING ||
          (invalidReasons &&
            Array.isArray(invalidReasons) &&
            invalidReasons.length !== 0) ||
          (softDiagnosisValidations &&
            Array.isArray(softDiagnosisValidations) &&
            softDiagnosisValidations.length !== 0)) &&
          !isPrelim && (
            <SignoutAlertModal
              {...{
                signOutCase,
                signoutAlert,
                setSignoutAlert,
                caseDetailObject,
                invalidReasons,
                softDiagnosisValidations,
              }}
              currentUser={user}
            />
          )}
      </div>
    );
  }
);

export default SignOutButton;
